import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/pro-solid-svg-icons";
import useLocationContext from "@/services/context/location/use-location-context.ts";
import useProjectFilterNumberContext from "@/services/context/project-filter-number/use-project-filter-number-context.ts";
import SimpleLoadingIndicator from "@/components/simple-loading-indicator";
import LocalisationSelector from "@/components/localisation-selector";
import SearchBar from "@/components/searchbar";
import useFilteredData from "@/utils/filter-data.ts";
import Button from "@/components/buttons/button";
import useProjectsContext from "@/services/context/projects/use-projects-context.ts";
import CardLayout from "@/layouts/cards";
import ProjectCard from "@/layouts/project-card";
import OrderBy from "@/components/order-by";
import useProjectOrderByContext from "@/services/context/project-order-by/use-project-order-by-context.ts";
import CreateProjectDialog from "@/components/create-project-dialog";

const ProjectsPage: React.FC = () => {
    const { projects, isLoading } = useProjectsContext();

    const { selectedLocations } = useLocationContext();

    const { filteredProjectNumber, setFilteredProjectNumber } = useProjectFilterNumberContext();

    const [searchQuery, setSearchQuery] = useState('');
    const { orderBy } = useProjectOrderByContext();


    const [showCreationProjectDialog, setShowCreationProjectDialog] = useState(false);


    // Première étape avec gestion des filtres de localisation.
    const localisationFilteredData = useMemo(() => {
        if (!projects || selectedLocations.length === 0) return projects;
        return projects.filter(project =>
            selectedLocations.includes(project.localisation.id)
        );
    }, [projects, selectedLocations]);

    // Deuxième étape qui utilise aussi la recherche utilisateur.
    const filteredData = useFilteredData({
        data: localisationFilteredData,
        searchQuery,
        searchFields: ['name']
    });

    useEffect(() => {
        let total = 0;
        if (filteredData)
            total = filteredData.length;
        else if (projects)
            total = projects.length;

        setFilteredProjectNumber(total);
    }, [filteredData, setFilteredProjectNumber, projects]);

    const sortedData = useMemo(() => {
        if (!filteredData) return [];

        const projectsToSort = [...filteredData];

        switch (orderBy) {
            case "name_asc":
                return projectsToSort.sort((a, b) => a.name.localeCompare(b.name));

            case "name_desc":
                return projectsToSort.sort((a, b) => b.name.localeCompare(a.name));

            case "date_asc":
                return projectsToSort.sort(
                    (a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime()
                );

            case "date_desc":
                return projectsToSort.sort(
                    (a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
                );

            default:
                return projectsToSort;
        }
    }, [filteredData, orderBy]);

    if (isLoading)
        return <SimpleLoadingIndicator text='Chargement des projets' />

    if (!projects)
        return <div>Erreur de chargement</div>

    return (
        <>
            <div className='flex flex-col gap-y-4 p-3 relative'>
                <div className='flex flex-row gap-x-4'>
                    <LocalisationSelector className={"flex gap-4 grow"} />

                    <SearchBar onSearch={setSearchQuery} />

                    <OrderBy />

                    <Button variant='simple' onClick={() => setShowCreationProjectDialog(true)}
                        data-tooltip-id='create-project-tooltip'>
                        <FontAwesomeIcon icon={faPlus} className='text-green-500' />
                    </Button>
                </div>

                <CardLayout cards={sortedData} Component={ProjectCard} />

                {projects &&
                    <div className='fixed bottom-0 right-4 flex-col gap-2'>
                        <div className='relative bottom-2 border bg-white rounded-lg py-1 px-2 shadow-lg'>
                            <div className="text-xs text-gray-500">
                                Affichage de {filteredProjectNumber} projets sur {projects.length}
                            </div>
                        </div>
                    </div>
                }
            </div>

            <CreateProjectDialog
                open={showCreationProjectDialog} title={'Créer un projet'}
                onClose={() => setShowCreationProjectDialog(false)} />
        </>
    );
};

export default ProjectsPage;